/* Analytics — Sleep/Stress daily entry + correlation dashboard + 24h heatmap. */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Moon, Fire, Waveform, ChartBar } from "@phosphor-icons/react";
import health from "./healthApi";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function Analytics() {
  const [today, setToday] = useState({ date: todayISO(), sleep_hours: 7, sleep_quality: 6, stress_level: 5, notes: "" });
  const [corr, setCorr] = useState(null);
  const [ss, setSs] = useState([]);

  const load = async () => {
    const [c, s] = await Promise.all([
      health.get("/analytics/correlation?days=90").then((r) => r.data),
      health.get("/sleep-stress?days=14").then((r) => r.data),
    ]);
    setCorr(c);
    setSs(s);
    const t = s.find((d) => d.date === todayISO());
    if (t) setToday({ ...today, ...t, date: todayISO() });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    try {
      await health.post("/sleep-stress", today);
      toast.success("Sleep & stress saved for today");
      load();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const maxHour = corr ? Math.max(1, ...(corr.hour_distribution || []).map((h) => h.count)) : 1;

  return (
    <div data-testid="nl-analytics-tab">
      <div className="mb-4">
        <div className="font-label text-[10px] tracking-widest" style={{ color: "var(--nl-accent)" }}>SLEEP & STRESS CORRELATION</div>
        <div className="font-display text-2xl font-black" style={{ color: "var(--nl-text)" }}>Pattern intelligence</div>
      </div>

      <div className="rounded-lg p-4 mb-4" style={{ background: "var(--nl-panel)", border: "1px solid var(--nl-line)" }}>
        <div className="font-label text-[10px] tracking-widest mb-3" style={{ color: "var(--nl-muted)" }}>TODAY'S ENTRY · {todayISO()}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Moon size={14} color="#48CAE4" />
              <span className="text-xs" style={{ color: "var(--nl-muted)" }}>Sleep last night: <b style={{ color: "var(--nl-accent)" }}>{today.sleep_hours}h</b></span>
            </div>
            <input type="range" min="0" max="14" step="0.5" value={today.sleep_hours} onChange={(e) => setToday({ ...today, sleep_hours: Number(e.target.value) })} className="w-full" />
            <div className="mt-3 text-xs" style={{ color: "var(--nl-muted)" }}>Sleep quality: <b style={{ color: "var(--nl-accent)" }}>{today.sleep_quality}/10</b></div>
            <input type="range" min="1" max="10" value={today.sleep_quality} onChange={(e) => setToday({ ...today, sleep_quality: Number(e.target.value) })} className="w-full" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Fire size={14} color="#F59E0B" />
              <span className="text-xs" style={{ color: "var(--nl-muted)" }}>Stress level today: <b style={{ color: "var(--nl-warn)" }}>{today.stress_level}/10</b></span>
            </div>
            <input type="range" min="1" max="10" value={today.stress_level} onChange={(e) => setToday({ ...today, stress_level: Number(e.target.value) })} className="w-full" />
            <textarea rows={2} value={today.notes} onChange={(e) => setToday({ ...today, notes: e.target.value })}
                      className="nl-input mt-3" style={{ resize: "none" }} placeholder="Notes (mood, caffeine, meds…)" />
          </div>
        </div>
        <button data-testid="nl-ss-save" onClick={save} className="mt-4 w-full h-10 rounded-lg font-label text-xs tracking-widest" style={{ background: "var(--nl-accent)", color: "#001018" }}>
          SAVE TODAY
        </button>
      </div>

      {corr && (
        <>
          <div className="grid grid-cols-3 gap-2 mb-4">
            <StatCard label="EVENTS · 90D" value={corr.total_events} />
            <StatCard label="AFTER POOR SLEEP" value={`${corr.preceded_by_poor_sleep_pct}%`} accent="warn" />
            <StatCard label="AFTER HIGH STRESS" value={`${corr.preceded_by_high_stress_pct}%`} accent="warn" />
          </div>

          {corr.top_triggers?.length > 0 && (
            <div className="rounded-lg p-4 mb-4" style={{ background: "var(--nl-panel)", border: "1px solid var(--nl-line)" }}>
              <div className="font-label text-[10px] tracking-widest mb-2" style={{ color: "var(--nl-muted)" }}>TOP TRIGGERS</div>
              <div className="space-y-2">
                {corr.top_triggers.map((t) => (
                  <div key={t.trigger} className="flex items-center gap-3">
                    <div className="text-xs w-40 truncate" style={{ color: "var(--nl-text)" }}>{t.trigger}</div>
                    <div className="flex-1 h-2 rounded-full" style={{ background: "var(--nl-line)" }}>
                      <div className="h-2 rounded-full" style={{ width: `${(t.count / corr.top_triggers[0].count) * 100}%`, background: "var(--nl-warn)" }} />
                    </div>
                    <div className="text-xs w-6 text-right" style={{ color: "var(--nl-warn)" }}>{t.count}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-lg p-4" style={{ background: "var(--nl-panel)", border: "1px solid var(--nl-line)" }}>
            <div className="font-label text-[10px] tracking-widest mb-3" style={{ color: "var(--nl-muted)" }}>TIME-OF-DAY DISTRIBUTION (24H)</div>
            <div className="flex items-end gap-1 h-24">
              {(corr.hour_distribution || []).map((h) => (
                <div key={h.hour} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full rounded-t" style={{ height: `${(h.count / maxHour) * 90}%`, background: h.count > 0 ? "var(--nl-accent)" : "var(--nl-line)", opacity: h.count > 0 ? 1 : 0.4 }} />
                </div>
              ))}
            </div>
            <div className="flex justify-between text-[10px] mt-1 font-mono" style={{ color: "var(--nl-muted)" }}>
              <span>00</span><span>06</span><span>12</span><span>18</span><span>23</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const StatCard = ({ label, value, accent = "accent" }) => (
  <div className="rounded-lg p-3 text-center" style={{ background: "var(--nl-panel)", border: "1px solid var(--nl-line)" }}>
    <div className="font-display font-black" style={{ color: accent === "warn" ? "var(--nl-warn)" : "var(--nl-accent)", fontSize: 22 }}>{value}</div>
    <div className="font-label text-[9px] tracking-widest mt-1" style={{ color: "var(--nl-muted)" }}>{label}</div>
  </div>
);
